/**
 * Dezente Rückmeldungen („Toasts") statt window.alert().
 *
 * Nutzung — ohne Provider, von überall, auch außerhalb von React:
 *
 *   import { toast } from "./ui/Toast";
 *
 *   toast("Regel gespeichert");                       // neutral, 4 s
 *   toast.erfolg("3 Nachrichten verschoben");          // grüner Punkt
 *   toast.fehler(err);                                  // Error, ApiError oder Text; bleibt 8 s
 *   toast.info("Neu von Anna", { dauer: 6000 });
 *   toast.erfolg("Übernommen", { aktion: { label: "Rückgängig", onClick: () => api.undo(id) } });
 *   const id = toast("Lädt …", { dauer: 0 });           // 0 = bleibt, bis toast.schliessen(id)
 *   toast.schliessen(id);
 *
 *   fehlerText(err)  → lesbarer Text aus beliebigem Fehlerwert.
 *
 * Der Container hängt sich beim ersten Aufruf selbst an document.body; er muss
 * nirgends eingebunden werden. Meldungen stehen in einer aria-live-Region,
 * Bildschirmleser lesen sie also vor.
 */
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";

export type ToastArt = "neutral" | "info" | "erfolg" | "fehler";

export interface ToastOptionen {
  art?: ToastArt;
  /** Anzeigedauer in ms; 0 = bleibt stehen. Standard 4000, Fehler 8000. */
  dauer?: number;
  /** Ein Knopf in der Meldung, z. B. „Rückgängig". Ein Klick schließt die Meldung. */
  aktion?: { label: string; onClick: () => void | Promise<unknown> };
}

interface Eintrag {
  id: number;
  text: string;
  art: ToastArt;
  dauer: number;
  aktion?: ToastOptionen["aktion"];
}

let eintraege: Eintrag[] = [];
let naechsteId = 1;
const hoerer = new Set<(e: Eintrag[]) => void>();
let montiert = false;

function melden() {
  for (const h of hoerer) h(eintraege);
}

function montieren() {
  if (montiert || typeof document === "undefined") return;
  montiert = true;
  const el = document.createElement("div");
  el.dataset.toastHost = "";
  document.body.appendChild(el);
  createRoot(el).render(<ToastHost />);
}

/** Lesbarer Text aus einem beliebigen Fehlerwert. */
export function fehlerText(err: unknown): string {
  if (err instanceof Error) {
    if (err.message === "Failed to fetch" || err.message === "NetworkError when attempting to fetch resource.") {
      return "Keine Verbindung zum Server.";
    }
    return err.message;
  }
  return typeof err === "string" ? err : "Unbekannter Fehler";
}

function zeigen(text: string, o: ToastOptionen = {}): number {
  montieren();
  const art = o.art ?? "neutral";
  const eintrag: Eintrag = {
    id: naechsteId++,
    text,
    art,
    dauer: o.dauer ?? (art === "fehler" ? 8000 : 4000),
    aktion: o.aktion,
  };
  // Höchstens vier gleichzeitig — ältere weichen.
  eintraege = [...eintraege, eintrag].slice(-4);
  melden();
  return eintrag.id;
}

function schliessen(id: number) {
  eintraege = eintraege.filter((e) => e.id !== id);
  melden();
}

type ToastFn = ((text: string, o?: ToastOptionen) => number) & {
  info: (text: string, o?: Omit<ToastOptionen, "art">) => number;
  erfolg: (text: string, o?: Omit<ToastOptionen, "art">) => number;
  fehler: (err: unknown, o?: Omit<ToastOptionen, "art">) => number;
  schliessen: (id: number) => void;
};

export const toast: ToastFn = Object.assign(zeigen, {
  info: (text: string, o?: Omit<ToastOptionen, "art">) => zeigen(text, { ...o, art: "info" }),
  erfolg: (text: string, o?: Omit<ToastOptionen, "art">) => zeigen(text, { ...o, art: "erfolg" }),
  fehler: (err: unknown, o?: Omit<ToastOptionen, "art">) => zeigen(fehlerText(err), { ...o, art: "fehler" }),
  schliessen,
});

function ToastHost() {
  const [liste, setListe] = useState<Eintrag[]>(eintraege);
  useEffect(() => {
    hoerer.add(setListe);
    return () => { hoerer.delete(setListe); };
  }, []);

  return (
    <div className="toast-stapel" role="region" aria-label="Meldungen" aria-live="polite">
      {liste.map((e) => <ToastZeile key={e.id} eintrag={e} />)}
    </div>
  );
}

function ToastZeile({ eintrag }: { eintrag: Eintrag }) {
  const [pausiert, setPausiert] = useState(false);

  useEffect(() => {
    if (eintrag.dauer <= 0 || pausiert) return;
    const t = window.setTimeout(() => schliessen(eintrag.id), eintrag.dauer);
    return () => window.clearTimeout(t);
  }, [eintrag, pausiert]);

  const Icon = eintrag.art === "fehler" ? CircleAlert : eintrag.art === "erfolg" ? CircleCheck : eintrag.art === "info" ? Info : null;

  return (
    <div
      className="toast"
      data-art={eintrag.art}
      role={eintrag.art === "fehler" ? "alert" : "status"}
      onMouseEnter={() => setPausiert(true)}
      onMouseLeave={() => setPausiert(false)}
      onFocus={() => setPausiert(true)}
      onBlur={() => setPausiert(false)}
    >
      {Icon && <Icon className="toast-icon" aria-hidden="true" />}
      <span className="toast-text">{eintrag.text}</span>
      {eintrag.aktion && (
        <button
          type="button"
          className="toast-aktion"
          onClick={async () => {
            schliessen(eintrag.id);
            try {
              await eintrag.aktion!.onClick();
            } catch (err) {
              toast.fehler(err);
            }
          }}
        >
          {eintrag.aktion.label}
        </button>
      )}
      <button type="button" className="toast-zu" aria-label="Meldung schließen" onClick={() => schliessen(eintrag.id)}>
        <X aria-hidden="true" />
      </button>
    </div>
  );
}
